import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import baseUrl from "../../../api/baseUrl";
import { validateSmsPromotionSettings } from "../../../utils/sms/validateSmsSettings";
import { SMS_SETTINGS_UPDATED_EVENT } from "../../../hooks/useBirthdayWishesEnabled";
import { ModalOverlay } from "../../../components/ModalOverlay";
import { PanelCard, SectionHeader } from "../components/accountUi";
import {
  isSmsTypeConfigured,
  MONTH_OPTIONS,
  PROMOTION_MESSAGE_TYPES,
  SMS_MESSAGE_TYPES,
  type PromotionMessageTypeConfig,
  type SmsMessageTypeConfig,
  type SmsSettingsData,
} from "./smsSettingsConfig";

type SmsSettingsFormValues = {
  welcomeMsg: string;
  appointmentMsg: string;
  appRescheduleMsg: string;
  appReminderMsg: string;
  appHourBeforeReminderMsg: string;
  morningReminderTime: string;
  invoiceMsg: string;
  birthdayMsg: string;
  birthdayEnabled: boolean;
  followUpMsg: string;
  followUpEnabled: boolean;
  followUpDaysAfter: number;
  holidayMsg: string;
  holidayEnabled: boolean;
  holidayDates: { name: string; date: string }[];
  oralHealthTipMsg: string;
  oralHealthTipEnabled: boolean;
  oralHealthTipDay: number;
  promoMsg: string;
  promoEnabled: boolean;
  promoMonths: number[];
  promoDay: number;
};

export type SmsSettingsPanelMode = "all" | "regular" | "promotional";

const REGULAR_SMS_FIELDS = [
  "welcomeMsg",
  "appointmentMsg",
  "appRescheduleMsg",
  "appReminderMsg",
  "appHourBeforeReminderMsg",
  "morningReminderTime",
  "invoiceMsg",
] as const;

const PROMOTIONAL_SMS_FIELDS = [
  "birthdayMsg",
  "birthdayEnabled",
  "followUpMsg",
  "followUpEnabled",
  "followUpDaysAfter",
  "holidayMsg",
  "holidayEnabled",
  "holidayDates",
  "oralHealthTipMsg",
  "oralHealthTipEnabled",
  "oralHealthTipDay",
  "promoMsg",
  "promoEnabled",
  "promoMonths",
  "promoDay",
] as const;

function pickFormValues(
  values: SmsSettingsFormValues,
  fields: readonly (keyof SmsSettingsFormValues)[]
) {
  return fields.reduce<Record<string, unknown>>((acc, field) => {
    acc[field] = values[field];
    return acc;
  }, {});
}

const inputClassName =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/15 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500";

const textareaClassName = `${inputClassName} min-h-[112px] resize-y leading-relaxed`;

function toFormValues(settings: SmsSettingsData): SmsSettingsFormValues {
  return {
    welcomeMsg: settings.welcomeMsg ?? "",
    appointmentMsg: settings.appointmentMsg ?? "",
    appRescheduleMsg: settings.appRescheduleMsg ?? "",
    appReminderMsg: settings.appReminderMsg ?? "",
    appHourBeforeReminderMsg: settings.appHourBeforeReminderMsg ?? "",
    morningReminderTime: settings.morningReminderTime ?? "08:00",
    invoiceMsg: settings.invoiceMsg ?? "",
    birthdayMsg: settings.birthdayMsg ?? "",
    birthdayEnabled: Boolean(settings.birthdayEnabled),
    followUpMsg: settings.followUpMsg ?? "",
    followUpEnabled: Boolean(settings.followUpEnabled),
    followUpDaysAfter: settings.followUpDaysAfter ?? 7,
    holidayMsg: settings.holidayMsg ?? "",
    holidayEnabled: Boolean(settings.holidayEnabled),
    holidayDates:
      Array.isArray(settings.holidayDates) && settings.holidayDates.length > 0
        ? settings.holidayDates
        : [{ name: "", date: "" }],
    oralHealthTipMsg: settings.oralHealthTipMsg ?? "",
    oralHealthTipEnabled: Boolean(settings.oralHealthTipEnabled),
    oralHealthTipDay: settings.oralHealthTipDay ?? 1,
    promoMsg: settings.promoMsg ?? "",
    promoEnabled: Boolean(settings.promoEnabled),
    promoMonths: Array.isArray(settings.promoMonths) ? settings.promoMonths : [],
    promoDay: settings.promoDay ?? 1,
  };
}

function SubsectionTitle({ children }: { children: string }) {
  return (
    <h4 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
      <span className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent dark:from-slate-600" />
      <span className="shrink-0">{children}</span>
      <span className="h-px flex-1 bg-gradient-to-l from-slate-200 to-transparent dark:from-slate-600" />
    </h4>
  );
}

function PlaceholderList({
  placeholders,
  onSelect,
}: {
  placeholders: string[];
  onSelect?: (placeholder: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {placeholders.map((placeholder) => (
        <button
          key={placeholder}
          type="button"
          onClick={() => onSelect?.(placeholder)}
          className={`rounded-md px-2 py-0.5 font-mono text-[11px] transition ${
            onSelect
              ? "bg-slate-100 text-slate-600 hover:bg-primary hover:text-white dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-primary"
              : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
          }`}
        >
          {placeholder}
        </button>
      ))}
    </div>
  );
}

function PromotionTemplateEditor({
  item,
  register,
  enabled,
  onToggleEnabled,
  setValue,
  watch,
}: {
  item: PromotionMessageTypeConfig;
  register: ReturnType<typeof useForm<SmsSettingsFormValues>>["register"];
  enabled: boolean;
  onToggleEnabled: (value: boolean) => void;
  setValue: ReturnType<typeof useForm<SmsSettingsFormValues>>["setValue"];
  watch: ReturnType<typeof useForm<SmsSettingsFormValues>>["watch"];
}) {
  const promoMonths = watch("promoMonths") || [];
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const {
    ref: registerTextareaRef,
    ...textareaField
  } = register(item.field);

  const togglePromoMonth = (month: number) => {
    const next = promoMonths.includes(month)
      ? promoMonths.filter((value) => value !== month)
      : [...promoMonths, month].sort((a, b) => a - b);
    setValue("promoMonths", next);
  };

  const insertPlaceholder = (placeholder: string) => {
    const textarea = textareaRef.current;
    const currentValue = String(watch(item.field) ?? "");
    const selectionStart = textarea?.selectionStart ?? currentValue.length;
    const selectionEnd = textarea?.selectionEnd ?? currentValue.length;
    const nextValue =
      currentValue.slice(0, selectionStart) +
      placeholder +
      currentValue.slice(selectionEnd);

    setValue(item.field, nextValue, {
      shouldDirty: true,
      shouldTouch: true,
    });

    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      const cursorPosition = selectionStart + placeholder.length;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-slate-50/40 p-4 dark:border-slate-600/80 dark:bg-slate-900/30">
      <div className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-white">{item.label}</p>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
          </div>
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => onToggleEnabled(event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30"
            />
            Enabled
          </label>
        </div>
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
              Available placeholders
            </p>
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              Click to insert
            </span>
          </div>
          <PlaceholderList
            placeholders={item.placeholders}
            onSelect={insertPlaceholder}
          />
        </div>
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
            Message template{enabled ? " *" : ""}
          </span>
          <textarea
            {...textareaField}
            ref={(element) => {
              registerTextareaRef(element);
              textareaRef.current = element;
            }}
            required={enabled}
            className={textareaClassName}
            placeholder={`Write your ${item.label.toLowerCase()} message…`}
          />
          {enabled ? (
            <span className="mt-1 block text-[11px] text-slate-400 dark:text-slate-500">
              Required while this message type is enabled.
            </span>
          ) : null}
        </label>
        {item.type === "follow-up" ? (
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
              Days after treatment completion
            </span>
            <input
              type="number"
              min={1}
              {...register("followUpDaysAfter", { valueAsNumber: true })}
              className={inputClassName}
            />
          </label>
        ) : null}
        {item.type === "oral-health-tip" ? (
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
              Day of month to send (1–28)
            </span>
            <input
              type="number"
              min={1}
              max={28}
              {...register("oralHealthTipDay", { valueAsNumber: true })}
              className={inputClassName}
            />
          </label>
        ) : null}
        {item.type === "promo" ? (
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                Promo send day of month (1–28)
              </span>
              <input
                type="number"
                min={1}
                max={28}
                {...register("promoDay", { valueAsNumber: true })}
                className={inputClassName}
              />
            </label>
            <div>
              <p className="mb-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                Slow period months
              </p>
              <div className="flex flex-wrap gap-1.5">
                {MONTH_OPTIONS.map((month) => (
                  <button
                    key={month.value}
                    type="button"
                    onClick={() => togglePromoMonth(month.value)}
                    className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
                      promoMonths.includes(month.value)
                        ? "bg-primary text-white"
                        : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                    }`}
                  >
                    {month.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}
        {item.type === "holiday" ? (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
              Holiday dates (MM-DD)
            </p>
            {[0, 1, 2].map((index) => (
              <div key={index} className="grid gap-2 sm:grid-cols-2">
                <input
                  {...register(`holidayDates.${index}.name`)}
                  className={inputClassName}
                  placeholder="Holiday name"
                />
                <input
                  {...register(`holidayDates.${index}.date`)}
                  className={inputClassName}
                  placeholder="MM-DD"
                />
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function PromotionMessageTypeCard({
  item,
  settings,
}: {
  item: PromotionMessageTypeConfig;
  settings: SmsSettingsData | null;
}) {
  const configured = isSmsTypeConfigured(settings, item.field);
  const enabled = Boolean(settings?.[item.enabledField]);

  return (
    <div
      className={`rounded-xl border p-4 transition ${
        configured && enabled
          ? "border-emerald-200/80 bg-emerald-50/40 dark:border-emerald-800/40 dark:bg-emerald-950/20"
          : "border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-800/30"
      }`}
    >
      <div className="flex items-start gap-3">
        {configured && enabled ? (
          <CheckCircleOutlineIcon
            sx={{ fontSize: 20 }}
            className="mt-0.5 text-emerald-600 dark:text-emerald-400"
          />
        ) : (
          <RadioButtonUncheckedIcon sx={{ fontSize: 20 }} className="mt-0.5 text-slate-400" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-slate-800 dark:text-white">{item.label}</p>
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                enabled
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                  : "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
              }`}
            >
              {enabled ? "Enabled" : "Disabled"}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                configured
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                  : "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
              }`}
            >
              {configured ? "Configured" : "Not configured"}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
          {configured ? (
            <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-xs leading-relaxed text-slate-600 dark:text-slate-300">
              {settings?.[item.field]}
            </p>
          ) : (
            <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
              Add a template to enable this promotion message.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function SmsTemplateEditor({
  item,
  register,
  setValue,
  watch,
}: {
  item: SmsMessageTypeConfig;
  register: ReturnType<typeof useForm<SmsSettingsFormValues>>["register"];
  setValue: ReturnType<typeof useForm<SmsSettingsFormValues>>["setValue"];
  watch: ReturnType<typeof useForm<SmsSettingsFormValues>>["watch"];
}) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const {
    ref: registerTextareaRef,
    ...textareaField
  } = register(item.field);

  const insertPlaceholder = (placeholder: string) => {
    const textarea = textareaRef.current;
    const currentValue = String(watch(item.field) ?? "");
    const selectionStart = textarea?.selectionStart ?? currentValue.length;
    const selectionEnd = textarea?.selectionEnd ?? currentValue.length;
    const nextValue =
      currentValue.slice(0, selectionStart) +
      placeholder +
      currentValue.slice(selectionEnd);

    setValue(item.field, nextValue, {
      shouldDirty: true,
      shouldTouch: true,
    });

    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      const cursorPosition = selectionStart + placeholder.length;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-slate-50/40 p-4 dark:border-slate-600/80 dark:bg-slate-900/30">
      <div className="space-y-3">
        <div>
          <p className="text-sm font-semibold text-slate-900 dark:text-white">{item.label}</p>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
        </div>
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
              Available placeholders
            </p>
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              Click to insert
            </span>
          </div>
          <PlaceholderList
            placeholders={item.placeholders}
            onSelect={insertPlaceholder}
          />
        </div>
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
            Message template
          </span>
          <textarea
            {...textareaField}
            ref={(element) => {
              registerTextareaRef(element);
              textareaRef.current = element;
            }}
            className={textareaClassName}
            placeholder={`Write your ${item.label.toLowerCase()} message…`}
          />
        </label>
      </div>
    </div>
  );
}

function SmsMessageTypeCard({
  item,
  settings,
}: {
  item: SmsMessageTypeConfig;
  settings: SmsSettingsData | null;
}) {
  const configured = isSmsTypeConfigured(settings, item.field);

  return (
    <div
      className={`rounded-xl border p-4 transition ${
        configured
          ? "border-emerald-200/80 bg-emerald-50/40 dark:border-emerald-800/40 dark:bg-emerald-950/20"
          : "border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-800/30"
      }`}
    >
      <div className="flex items-start gap-3">
        {configured ? (
          <CheckCircleOutlineIcon
            sx={{ fontSize: 20 }}
            className="mt-0.5 text-emerald-600 dark:text-emerald-400"
          />
        ) : (
          <RadioButtonUncheckedIcon sx={{ fontSize: 20 }} className="mt-0.5 text-slate-400" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-slate-800 dark:text-white">{item.label}</p>
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                configured
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                  : "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
              }`}
            >
              {configured ? "Configured" : "Not configured"}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
          {configured ? (
            <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-xs leading-relaxed text-slate-600 dark:text-slate-300">
              {settings?.[item.field]}
            </p>
          ) : (
            <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
              This message type will not be sent until a template is added.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

type SmsSettingsModalProps = {
  open: boolean;
  settings: SmsSettingsData | null;
  editing: boolean;
  saving: boolean;
  saveError: string | null;
  mode: SmsSettingsPanelMode;
  methods: ReturnType<typeof useForm<SmsSettingsFormValues>>;
  onClose: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
};

function SmsSettingsModal({
  open,
  settings,
  editing,
  saving,
  saveError,
  mode,
  methods,
  onClose,
  onStartEdit,
  onCancelEdit,
  onSave,
}: SmsSettingsModalProps) {
  if (!open) return null;

  const regularOnly = mode === "regular";
  const promotionalOnly = mode === "promotional";
  const modalTitle = editing
    ? regularOnly
      ? "Edit regular SMS templates"
      : promotionalOnly
        ? "Edit promotional SMS templates"
        : "Edit SMS templates"
    : regularOnly
      ? "Regular SMS message types"
      : promotionalOnly
        ? "Promotional SMS message types"
        : "SMS message types";
  const modalSubtitle = editing
    ? regularOnly
      ? "Update reminder timing and regular message templates"
      : promotionalOnly
        ? "Update promotional templates and schedules"
        : "Update reminder time and message templates"
    : regularOnly
      ? "Templates used for operational SMS from your clinic"
      : promotionalOnly
        ? "Templates used for promotional SMS from your clinic"
        : "Templates used when your clinic sends SMS messages";

  const handleBackdropClick = () => {
    if (saving) return;
    if (editing) onCancelEdit();
    else onClose();
  };

  return (
    <ModalOverlay
      className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-900/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:rounded-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sms-settings-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
          <div className="min-w-0">
            <h2
              id="sms-settings-modal-title"
              className="text-base font-bold text-slate-900 dark:text-white"
            >
              {modalTitle}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {modalSubtitle}
            </p>
          </div>
          <button
            type="button"
            onClick={editing ? onCancelEdit : onClose}
            disabled={saving}
            className="inline-flex shrink-0 items-center justify-center rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            aria-label={editing ? "Cancel editing" : "Close"}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {editing ? (
            <FormProvider {...methods}>
              <form id="sms-settings-form" onSubmit={onSave} className="space-y-6">
                {promotionalOnly ? null : (
                <section className="space-y-4">
                  <SubsectionTitle>General</SubsectionTitle>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-600 dark:bg-slate-800/40">
                      <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                        Sender ID
                      </p>
                      <p className="mt-1.5 text-sm font-semibold text-slate-800 dark:text-white">
                        {settings?.senderId?.trim() || "Not set"}
                      </p>
                      <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
                        Contact your platform admin to change this.
                      </p>
                    </div>
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                        Morning reminder time
                      </span>
                      <input
                        {...methods.register("morningReminderTime")}
                        className={inputClassName}
                        placeholder="08:00"
                      />
                      <span className="mt-1 block text-[11px] text-slate-400 dark:text-slate-500">
                        Asia/Colombo timezone
                      </span>
                    </label>
                  </div>
                </section>
                )}

                {promotionalOnly ? null : (
                <section className="space-y-4">
                  <SubsectionTitle>Message templates</SubsectionTitle>
                  <div className="space-y-3">
                    {SMS_MESSAGE_TYPES.map((item) => (
                      <SmsTemplateEditor
                        key={item.field}
                        item={item}
                        register={methods.register}
                        setValue={methods.setValue}
                        watch={methods.watch}
                      />
                    ))}
                  </div>
                </section>
                )}

                {regularOnly ? null : (
                <section className="space-y-4">
                  <SubsectionTitle>Promotion messages</SubsectionTitle>
                  <div className="space-y-3">
                    {PROMOTION_MESSAGE_TYPES.map((item) => (
                      <PromotionTemplateEditor
                        key={item.field}
                        item={item}
                        register={methods.register}
                        enabled={Boolean(methods.watch(item.enabledField))}
                        onToggleEnabled={(value) => methods.setValue(item.enabledField, value)}
                        setValue={methods.setValue}
                        watch={methods.watch}
                      />
                    ))}
                  </div>
                </section>
                )}

                {saveError ? (
                  <div className="rounded-xl border border-red-200/80 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-950/30 dark:text-red-300">
                    {saveError}
                  </div>
                ) : null}
              </form>
            </FormProvider>
          ) : (
            <div className="space-y-4">
              {promotionalOnly ? null : (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-600 dark:bg-slate-800/40">
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Sender ID</p>
                  <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-white">
                    {settings?.senderId?.trim() || "Not set"}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-600 dark:bg-slate-800/40">
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    Morning reminder time
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-white">
                    {settings?.morningReminderTime || "08:00"} (Asia/Colombo)
                  </p>
                </div>
              </div>
              )}

              {promotionalOnly ? null : (
              <div className="grid gap-3 lg:grid-cols-2">
                {SMS_MESSAGE_TYPES.map((item) => (
                  <SmsMessageTypeCard key={item.field} item={item} settings={settings} />
                ))}
              </div>
              )}

              {regularOnly ? null : (
                <>
                  <SubsectionTitle>Promotion messages</SubsectionTitle>
                  <div className="grid gap-3 lg:grid-cols-2">
                    {PROMOTION_MESSAGE_TYPES.map((item) => (
                      <PromotionMessageTypeCard key={item.field} item={item} settings={settings} />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-slate-100 bg-white px-5 py-4 dark:border-slate-700/80 dark:bg-slate-900">
          {editing ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={onCancelEdit}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="sms-settings-form"
                disabled={saving}
                className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary/20 transition hover:bg-primary/90 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Close
              </button>
              <button
                type="button"
                onClick={onStartEdit}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary/20 transition hover:bg-primary/90"
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
                Edit templates
              </button>
            </div>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}

export default function SmsSettingsPanel({
  mode = "all",
  promotionalLocked = false,
  onSaved,
}: {
  mode?: SmsSettingsPanelMode;
  promotionalLocked?: boolean;
  onSaved?: () => void;
}) {
  const [settings, setSettings] = useState<SmsSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const methods = useForm<SmsSettingsFormValues>({
    defaultValues: {
      morningReminderTime: "08:00",
      welcomeMsg: "",
      appointmentMsg: "",
      appRescheduleMsg: "",
      appReminderMsg: "",
      appHourBeforeReminderMsg: "",
      invoiceMsg: "",
      birthdayMsg: "",
      birthdayEnabled: false,
      followUpMsg: "",
      followUpEnabled: false,
      followUpDaysAfter: 7,
      holidayMsg: "",
      holidayEnabled: false,
      holidayDates: [{ name: "", date: "" }],
      oralHealthTipMsg: "",
      oralHealthTipEnabled: false,
      oralHealthTipDay: 1,
      promoMsg: "",
      promoEnabled: false,
      promoMonths: [],
      promoDay: 1,
    },
  });

  const regularOnly = mode === "regular";
  const promotionalOnly = mode === "promotional";
  const panelTitle = regularOnly
    ? "Regular SMS templates"
    : promotionalOnly
      ? "Promotional SMS templates"
      : "SMS message types";
  const viewButtonLabel = regularOnly
    ? "View regular templates"
    : promotionalOnly
      ? "View promotional types"
      : "View message types";

  const closeModal = useCallback(() => {
    if (settings) methods.reset(toFormValues(settings));
    setEditing(false);
    setSaveError(null);
    setModalOpen(false);
  }, [methods, settings]);

  const cancelEditing = useCallback(() => {
    if (settings) methods.reset(toFormValues(settings));
    setEditing(false);
    setSaveError(null);
  }, [methods, settings]);

  const loadSettings = useCallback(() => {
    setLoading(true);
    setError(null);
    return baseUrl
      .get("/client/account/sms-settings")
      .then((res) => {
        if (res?.data?.success) {
          const data = res.data.data as SmsSettingsData;
          setSettings(data);
          methods.reset(toFormValues(data));
        } else {
          setSettings(null);
          setError("Could not load SMS settings.");
        }
      })
      .catch(() => {
        setSettings(null);
        setError("Could not load SMS settings.");
      })
      .finally(() => setLoading(false));
  }, [methods]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = methods.handleSubmit(async (values) => {
    const currentValues = settings ? toFormValues(settings) : values;
    const regularPayload = pickFormValues(values, REGULAR_SMS_FIELDS);
    const promotionalPayload = {
      ...pickFormValues(values, PROMOTIONAL_SMS_FIELDS),
      holidayDates: (values.holidayDates || []).filter((item) => item.date?.trim()),
    };

    const payload = regularOnly
      ? regularPayload
      : promotionalOnly
        ? {
            ...pickFormValues(currentValues, REGULAR_SMS_FIELDS),
            ...promotionalPayload,
          }
        : {
            ...regularPayload,
            ...promotionalPayload,
          };

    if (!regularOnly) {
      const validationErrors = validateSmsPromotionSettings(promotionalPayload);
      if (validationErrors.length > 0) {
        setSaveError(validationErrors[0]);
        return;
      }
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const res = await baseUrl.put("/client/account/sms-settings", {
        ...payload,
        senderId: settings?.senderId ?? "",
      });
      if (res?.data?.success) {
        const data = res.data.data as SmsSettingsData;
        setSettings(data);
        methods.reset(toFormValues(data));
        setEditing(false);
        setSaveSuccess(true);
        window.dispatchEvent(new Event(SMS_SETTINGS_UPDATED_EVENT));
        onSaved?.();
      } else {
        setSaveError("Could not save SMS settings.");
      }
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || "Could not save SMS settings.");
    } finally {
      setSaving(false);
    }
  });

  const configuredCount =
    (promotionalOnly
      ? 0
      : SMS_MESSAGE_TYPES.filter((item) => isSmsTypeConfigured(settings, item.field)).length) +
    (regularOnly
      ? 0
      : PROMOTION_MESSAGE_TYPES.filter((item) => isSmsTypeConfigured(settings, item.field)).length);
  const totalTypes =
    (promotionalOnly ? 0 : SMS_MESSAGE_TYPES.length) +
    (regularOnly ? 0 : PROMOTION_MESSAGE_TYPES.length);
  const subtitle = promotionalLocked
    ? "Locked on your current clinic plan"
    : `${configuredCount} of ${totalTypes} types configured`;

  return (
    <>
      <PanelCard className="overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <SectionHeader
              icon={SettingsOutlinedIcon}
              title={panelTitle}
              subtitle={subtitle}
            />
            {!loading && !error && !promotionalLocked ? (
              <button
                type="button"
                onClick={() => {
                  setSaveSuccess(false);
                  setEditing(false);
                  setSaveError(null);
                  setModalOpen(true);
                }}
                className="inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-xl border border-primary px-4 py-2 text-xs font-semibold text-primary transition hover:bg-primary/5"
              >
                <SettingsOutlinedIcon sx={{ fontSize: 16 }} />
                {viewButtonLabel}
              </button>
            ) : null}
          </div>
        </div>

        <div className="p-5">
          {loading ? (
            <p className="text-sm text-slate-500">Loading SMS settings…</p>
          ) : error ? (
            <div className="rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200">
              {error}
            </div>
          ) : promotionalLocked ? (
            <div className="rounded-2xl border border-amber-200/80 bg-amber-50/80 p-5 dark:border-amber-800/50 dark:bg-amber-950/20">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                  <LockOutlinedIcon sx={{ fontSize: 20 }} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                    Promotional SMS is locked for this clinic
                  </p>
                  <p className="mt-1 text-sm text-amber-800 dark:text-amber-200">
                    Birthday wishes, follow-up messages, holiday greetings, oral-health tips,
                    and promotional offers are available only for clinics with the promotional SMS
                    feature enabled by admin.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {saveSuccess ? (
                <div className="rounded-xl border border-emerald-200/80 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-800/50 dark:bg-emerald-950/30 dark:text-emerald-200">
                  SMS settings saved successfully.
                </div>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-3">
                {!promotionalOnly ? (
                  <>
                    <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-600 dark:bg-slate-800/40">
                      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Sender ID</p>
                      <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-white">
                        {settings?.senderId?.trim() || "Not set"}
                      </p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-600 dark:bg-slate-800/40">
                      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                        Morning reminder
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-white">
                        {settings?.morningReminderTime || "08:00"}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-600 dark:bg-slate-800/40">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                      Promotional access
                    </p>
                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-white">
                      Enabled
                    </p>
                  </div>
                )}
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-600 dark:bg-slate-800/40">
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    Templates configured
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-white">
                    {configuredCount} / {totalTypes}
                  </p>
                </div>
              </div>

              <p className="text-xs text-slate-500 dark:text-slate-400">
                {regularOnly
                  ? "Open the modal to view and edit regular SMS templates."
                  : promotionalOnly
                    ? "Open the modal to view and edit promotional SMS templates."
                    : "Open the modal to view all message types and edit templates."}
              </p>
            </div>
          )}
        </div>
      </PanelCard>

      <SmsSettingsModal
        open={modalOpen}
        settings={settings}
        editing={editing}
        saving={saving}
        saveError={saveError}
        mode={mode}
        methods={methods}
        onClose={closeModal}
        onStartEdit={() => {
          setSaveError(null);
          setEditing(true);
        }}
        onCancelEdit={cancelEditing}
        onSave={handleSave}
      />
    </>
  );
}
